import { useCallback, useState, type Dispatch, type MutableRefObject, type SetStateAction } from 'react';
import type { SetURLSearchParams } from 'react-router-dom';
import type { UseFormGetValues, UseFormReset, UseFormSetValue } from 'react-hook-form';

import type { Clinic } from '@app-types/auth';
import type { ToastMethods } from '@/hooks/useAppToast';
import type useAppModal from '@/hooks/useAppModal';
import { computeBulkHistoryBdDraftAdjustments } from '@pages/main/patient/utils/bulkHistoryBdTimeframe';
import { computeSinglePatientInformationHistoryNewAvailableToYmd } from '@pages/main/patient/utils/patientInfoSingleHistoryBd';
import {
  buildPatientEditHistorySearchParams,
  PATIENT_EDIT_HISTORY_SEARCH,
} from '@pages/main/patient/utils/patientEditHistoryUrl';
import { validateBulkHistoryDate } from '@pages/main/patient/services';
import type { PatientFormData, PatientFormSchemaType } from '@pages/main/patient/types';
import {
  healthInsuranceCardValuesForNewHistoryDraft,
  patientInformationValuesForNewHistoryDraft,
} from './usePatientEditPage.history';
import { normalizePatientData, toFormValues } from './usePatientEditPage.formValues';
import type { PatientHistoryType } from './usePatientEditPage.types';

type AppModal = ReturnType<typeof useAppModal>;

interface UsePatientEditPageHistoryWorkflowArgs {
  patientData: PatientFormData | null;
  patientQueryNumericId?: number;
  refetchPatient: () => Promise<{ data?: PatientFormData; error?: unknown }>;
  clinic: Clinic | null;
  getValues: UseFormGetValues<PatientFormSchemaType>;
  reset: UseFormReset<PatientFormSchemaType>;
  setValue: UseFormSetValue<PatientFormSchemaType>;
  setPatientData: Dispatch<SetStateAction<PatientFormData | null>>;
  setInitialFormValuesForGuard: Dispatch<SetStateAction<PatientFormSchemaType | null>>;
  syncOriginalCurrentIds: (data: PatientFormData) => void;
  setHistorySearchParamsRef: MutableRefObject<SetURLSearchParams | undefined>;
  historySearchParamsRef: MutableRefObject<URLSearchParams | undefined>;
  deleteHistory: {
    mutate: (
      variables: { patientId: number; type: PatientHistoryType; historyId: number },
      options?: { onSuccess?: () => void }
    ) => void;
  };
  modal: AppModal;
  toast: ToastMethods;
}

export const usePatientEditPageHistoryWorkflow = ({
  patientData,
  patientQueryNumericId,
  refetchPatient,
  clinic,
  getValues,
  reset,
  setValue,
  setPatientData,
  setInitialFormValuesForGuard,
  syncOriginalCurrentIds,
  setHistorySearchParamsRef,
  historySearchParamsRef,
  deleteHistory,
  modal,
  toast,
}: UsePatientEditPageHistoryWorkflowArgs) => {
  const [isPatientInfoValidityModalOpen, setIsPatientInfoValidityModalOpen] = useState(false);
  const [singleHistoryAvailableToMinus, setSingleHistoryAvailableToMinus] = useState<
    string | undefined
  >(undefined);
  /** BD: X = 保険証資格取得日 from modal; sent in options for server-side `patient_information.available_to`. */
  const [singleHistoryQualificationDateYmd, setSingleHistoryQualificationDateYmd] = useState<
    string | undefined
  >(undefined);
  const [isBulkHistoryDateModalOpen, setIsBulkHistoryDateModalOpen] = useState(false);
  const [isNewHistoryMode, setIsNewHistoryMode] = useState(false);

  const handleHistorySwitch = useCallback(
    (type: PatientHistoryType, historyId: number) => {
      if (!patientData?.id) {
        toast.error('患者IDが不明です');
        return;
      }

      // Special case: "新しい情報を入力する" (Create new history) - historyId = 0
      if (historyId === 0) {
        modal.confirm({
          title:
            type === 'patient_information'
              ? '新しく受診者情報履歴を作成しますか？'
              : type === 'health_insurance_card'
                ? '新しく保険証情報履歴を作成しますか？'
                : '新しく医療助成情報履歴を作成しますか？',
          okText: 'OK',
          cancelText: 'キャンセル',
          onOk: () => {
            if (type === 'patient_information') {
              setIsPatientInfoValidityModalOpen(true);
            } else {
              const currentFormValues = getValues();

              if (type === 'health_insurance_card') {
                const newFormValues: PatientFormSchemaType = {
                  ...currentFormValues,
                  health_insurance_card: {
                    ...healthInsuranceCardValuesForNewHistoryDraft(
                      currentFormValues.health_insurance_card
                    ),
                    qualification_date: '',
                    expiration_date: '',
                  },
                };
                setInitialFormValuesForGuard(newFormValues);
                reset(newFormValues, {
                  keepDirty: false,
                  keepTouched: false,
                  keepDefaultValues: false,
                });
                setValue('health_insurance_card.id', undefined, {
                  shouldValidate: false,
                  shouldDirty: true,
                });
                const piId = patientData.current_patient_information?.id;
                const maId = patientData.medical_aids?.find(a => a.is_current)?.id;
                setHistorySearchParamsRef.current?.(
                  buildPatientEditHistorySearchParams({
                    patientInformationId: piId ?? null,
                    healthInsuranceCardId: null,
                    medicalAidId: maId ?? null,
                  }),
                  { replace: true }
                );
              } else if (type === 'medical_aid') {
                const newFormValues = {
                  ...currentFormValues,
                  medical_aid: {
                    ...currentFormValues.medical_aid,
                    id: undefined,
                    application_start_date: '',
                    application_end_date: '',
                  },
                };
                setInitialFormValuesForGuard(newFormValues);
                reset(newFormValues, {
                  keepDirty: false,
                  keepTouched: false,
                });
                const piId = patientData.current_patient_information?.id;
                const hicId = patientData.health_insurance_cards?.find(c => c.is_current)?.id;
                setHistorySearchParamsRef.current?.(
                  buildPatientEditHistorySearchParams({
                    patientInformationId: piId ?? null,
                    healthInsuranceCardId: hicId ?? null,
                    medicalAidId: null,
                  }),
                  { replace: true }
                );
              }
            }
          },
        });
        return;
      }

      const activeHistoryId =
        type === 'patient_information'
          ? patientData.current_patient_information?.id
          : type === 'health_insurance_card'
            ? patientData.health_insurance_cards?.find(c => c.is_current)?.id
            : patientData.medical_aids?.find(a => a.is_current)?.id;

      if (activeHistoryId != null && historyId === activeHistoryId) {
        return;
      }

      const switchConfirmTitle =
        type === 'patient_information'
          ? '受診者情報を切り替えますか？'
          : type === 'health_insurance_card'
            ? '保険証情報を切り替えますか？'
            : '医療助成情報を切り替えますか？';

      modal.confirm({
        title: switchConfirmTitle,
        okText: 'OK',
        cancelText: 'キャンセル',
        onOk: () => {
          const pi =
            type === 'patient_information'
              ? historyId
              : patientData.current_patient_information?.id;
          const hic =
            type === 'health_insurance_card'
              ? historyId
              : patientData.health_insurance_cards?.find(c => c.is_current)?.id;
          const ma =
            type === 'medical_aid'
              ? historyId
              : patientData.medical_aids?.find(a => a.is_current)?.id;
          setHistorySearchParamsRef.current?.(
            buildPatientEditHistorySearchParams({
              patientInformationId: pi ?? null,
              healthInsuranceCardId: hic ?? null,
              medicalAidId: ma ?? null,
            }),
            { replace: true }
          );
        },
      });
    },
    [patientData, reset, setValue, getValues, modal, toast, setInitialFormValuesForGuard, setHistorySearchParamsRef]
  );

  const handleHistoryDelete = useCallback(
    (type: PatientHistoryType, historyId: number) => {
      if (!patientData?.id) {
        toast.error('患者IDが不明です');
        return;
      }

      modal.confirm({
        title: '本当に現在表示中の受診者情報履歴を削除しますか?',
        okType: 'danger',
        onOk: () => {
          deleteHistory.mutate(
            {
              patientId: patientData.id,
              type,
              historyId,
            },
            {
              onSuccess: () => {
                const keyMap = {
                  patient_information: PATIENT_EDIT_HISTORY_SEARCH.patient_information_id,
                  health_insurance_card: PATIENT_EDIT_HISTORY_SEARCH.health_insurance_card_id,
                  medical_aid: PATIENT_EDIT_HISTORY_SEARCH.medical_aid_id,
                } as const;
                const next = new URLSearchParams(historySearchParamsRef.current ?? undefined);
                next.delete(keyMap[type]);
                setHistorySearchParamsRef.current?.(next, { replace: true });
              },
            }
          );
        },
      });
    },
    [patientData, deleteHistory, modal, toast, historySearchParamsRef, setHistorySearchParamsRef]
  );

  const handleBulkCreateHistory = useCallback(async () => {
    if (!patientData?.id) {
      toast.error('患者IDが不明です');
      return;
    }
    modal.confirm({
      title: 'まとめて新しい履歴を作成しますか？',
      okText: 'OK',
      cancelText: 'キャンセル',
      onOk: () => {
        setIsBulkHistoryDateModalOpen(true);
      },
    });
  }, [patientData?.id, modal, toast]);

  const handleBulkHistoryDateConfirm = useCallback(
    async (qualificationDate: string): Promise<string | null> => {
      if (!patientData || !qualificationDate) {
        toast.error('更新ができませんでした。');
        return '更新ができませんでした。';
      }

      if (patientData.id && clinic?.site_type && clinic?.clinic_number) {
        try {
          await validateBulkHistoryDate(
            clinic.site_type,
            clinic.clinic_number,
            patientData.id,
            qualificationDate
          );
        } catch (e: any) {
          const beErrors = e?.response?.data?.errors;
          if (beErrors && typeof beErrors === 'object') {
            const msgs = Object.values(beErrors).flat();
            const firstMsg = msgs[0];
            if (firstMsg) return String(firstMsg);
          }
          return '更新ができませんでした。';
        }
      }

      const dateObj = new Date(qualificationDate);
      dateObj.setDate(dateObj.getDate() - 1);
      const availableToMinus = dateObj.toISOString().split('T')[0];

      const currentFormValues = getValues();
      const insurerTypeId =
        patientData.medical_aids?.find(a => a.is_current)?.insurer_type_id ??
        currentFormValues.medical_aid?.insurer_type_id;

      const bd = computeBulkHistoryBdDraftAdjustments(patientData, qualificationDate);

      const newFormValues: PatientFormSchemaType = {
        ...currentFormValues,
        patient_information: {
          ...patientInformationValuesForNewHistoryDraft(currentFormValues.patient_information),
          available_from: qualificationDate,
          available_to: bd.patientInformationAvailableTo,
        },
        health_insurance_card: {
          ...healthInsuranceCardValuesForNewHistoryDraft(currentFormValues.health_insurance_card),
          qualification_date: bd.healthInsuranceQualificationDate,
          expiration_date: bd.healthInsuranceExpirationDate,
        },
        medical_aid: {
          ...currentFormValues.medical_aid,
          ...(insurerTypeId !== 1
            ? {
                id: undefined,
                application_start_date: bd.medicalAidApplicationStartDate,
                application_end_date: bd.medicalAidApplicationEndDate,
              }
            : {}),
        },
      };

      newFormValues._bulkHistoryAvailableToMinus = availableToMinus;
      newFormValues._bulkHistoryQualificationDate = qualificationDate;

      setInitialFormValuesForGuard(newFormValues);
      reset(newFormValues, {
        keepDirty: false,
        keepTouched: false,
        keepDefaultValues: false,
      });
      setValue('patient_information.id', undefined, { shouldValidate: false, shouldDirty: true });
      setValue('health_insurance_card.id', undefined, { shouldValidate: false, shouldDirty: true });
      setIsNewHistoryMode(true);
      setIsBulkHistoryDateModalOpen(false);
      const anchorMaId = patientData.medical_aids?.find(a => a.is_current)?.id;
      setHistorySearchParamsRef.current?.(
        buildPatientEditHistorySearchParams({
          patientInformationId: null,
          healthInsuranceCardId: null,
          medicalAidId: insurerTypeId === 1 ? (anchorMaId ?? null) : null,
          type: 'all_change',
          bulkQualificationDate: qualificationDate,
        }),
        { replace: true }
      );
      return null;
    },
    [patientData, clinic, getValues, reset, setValue, setInitialFormValuesForGuard, setHistorySearchParamsRef, toast]
  );

  const handleBulkHistoryDateModalClose = useCallback(() => {
    setIsBulkHistoryDateModalOpen(false);
  }, []);

  const handlePatientInfoValidityModalClose = useCallback(() => {
    setIsPatientInfoValidityModalOpen(false);
  }, []);

  const handlePatientHistoryCreateCancel = useCallback(async () => {
    if (!patientQueryNumericId) {
      return;
    }
    setHistorySearchParamsRef.current?.(new URLSearchParams(), { replace: true });
    setIsNewHistoryMode(false);
    setSingleHistoryAvailableToMinus(undefined);
    setSingleHistoryQualificationDateYmd(undefined);
    const { data, error } = await refetchPatient();
    if (error || !data) {
      toast.error('患者データの再読み込みに失敗しました');
      return;
    }
    const normalizedData = normalizePatientData(data);
    const formValues = toFormValues(normalizedData);
    setPatientData(normalizedData);
    syncOriginalCurrentIds(normalizedData);
    setInitialFormValuesForGuard(formValues);
    reset(formValues, { keepDirty: false, keepTouched: false });
  }, [
    patientQueryNumericId,
    refetchPatient,
    reset,
    toast,
    syncOriginalCurrentIds,
    setHistorySearchParamsRef,
    setPatientData,
    setInitialFormValuesForGuard,
  ]);

  const handlePatientInfoValidityModalConfirm = useCallback(
    async (_date: string): Promise<string | null> => {
      if (!patientData) {
        toast.error('患者データが読み込まれていません');
        return '患者データが読み込まれていません';
      }

      const trimmed = String(_date ?? '').trim();
      if (!trimmed) {
        toast.error('更新ができませんでした。');
        return '更新ができませんでした。';
      }

      const xSlice = trimmed.slice(0, 10);
      const xDay = new Date(`${xSlice}T12:00:00`);
      if (Number.isNaN(xDay.getTime())) {
        toast.error('日付が正しくありません');
        return '日付が正しくありません';
      }
      const xYmd = xDay.toISOString().split('T')[0];

      if (patientData.id && clinic?.site_type && clinic?.clinic_number) {
        try {
          await validateBulkHistoryDate(
            clinic.site_type,
            clinic.clinic_number,
            patientData.id,
            xYmd,
            'patient_information'
          );
        } catch (e: any) {
          const beErrors = e?.response?.data?.errors;
          if (beErrors && typeof beErrors === 'object') {
            const msgs = Object.values(beErrors).flat();
            const firstMsg = msgs[0];
            if (firstMsg) return String(firstMsg);
          }
          return '更新ができませんでした。';
        }
      }

      const minusDay = new Date(xDay);
      minusDay.setDate(minusDay.getDate() - 1);
      const availableToMinus = minusDay.toISOString().split('T')[0];

      const newPiAvailableTo = computeSinglePatientInformationHistoryNewAvailableToYmd(
        patientData,
        xYmd
      );

      const currentFormValues = getValues();
      const newHistoryValues: PatientFormSchemaType = {
        ...currentFormValues,
        patient_information: {
          ...patientInformationValuesForNewHistoryDraft(currentFormValues.patient_information),
          available_from: xYmd,
          available_to: newPiAvailableTo,
        },
      };

      setInitialFormValuesForGuard(newHistoryValues);
      reset(newHistoryValues, {
        keepDirty: false,
        keepTouched: false,
        keepDefaultValues: false,
      });
      setValue('patient_information.id', undefined, { shouldValidate: false, shouldDirty: true });

      const anchorHicId = patientData.health_insurance_cards?.find(c => c.is_current)?.id;
      const anchorMaId = patientData.medical_aids?.find(a => a.is_current)?.id;
      setHistorySearchParamsRef.current?.(
        buildPatientEditHistorySearchParams({
          patientInformationId: null,
          healthInsuranceCardId: anchorHicId ?? null,
          medicalAidId: anchorMaId ?? null,
        }),
        { replace: true }
      );

      setSingleHistoryAvailableToMinus(availableToMinus);
      setSingleHistoryQualificationDateYmd(xYmd);
      setIsPatientInfoValidityModalOpen(false);
      return null;
    },
    [reset, setValue, patientData, getValues, toast, clinic, setInitialFormValuesForGuard, setHistorySearchParamsRef]
  );

  return {
    isPatientInfoValidityModalOpen,
    isBulkHistoryDateModalOpen,
    isNewHistoryMode,
    singleHistoryAvailableToMinus,
    singleHistoryQualificationDateYmd,
    setIsNewHistoryMode,
    setSingleHistoryAvailableToMinus,
    setSingleHistoryQualificationDateYmd,
    handleHistorySwitch,
    handleHistoryDelete,
    handleBulkCreateHistory,
    handleBulkHistoryDateConfirm,
    handleBulkHistoryDateModalClose,
    handlePatientInfoValidityModalClose,
    handlePatientInfoValidityModalConfirm,
    handlePatientHistoryCreateCancel,
  };
};
